import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomCheckbox from '../Components/OsloKommuneUI/OKomCheckbox';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';

const AGE_BANDS: { ages: number[]; background: string }[] = [
  { ages: [4, 5, 6],                 background: '#f5b7c0' },
  { ages: [7, 8, 9, 10],             background: '#f5d7dc' },
  { ages: [11, 12, 13],              background: '#bfdff0' },
  { ages: [14, 15, 16, 17, 18],      background: '#f6d2a3' },
];

const backgroundForAge = (age: number): string => {
  for (const band of AGE_BANDS) {
    if (band.ages.includes(age)) return band.background;
  }
  return '#fff';
};

const MAX_YEAR = 2035;

const AthleticsRegistrationConfigView: React.FC<RouteComponentProps> = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [events, setEvents] = useState<Api.AthleticsEventDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const isReadOnly = year < currentYear;
  const allYears = Array.from(
    { length: Math.max(1, MAX_YEAR - currentYear + 1) },
    (_, i) => currentYear + i,
  );

  useEffect(() => {
    document.title = 'Konfigurasjon — Friidrettsregistrering';
  }, []);

  const loadEvents = async (forYear: number) => {
    setIsLoading(true);
    try {
      const res = await RestService.get<Api.AthleticsEventDto[]>(`/api/athleticsregistration/GetEvents?year=${forYear}`);
      setEvents(res ?? []);
    } catch (e) {
      toast.error(`Kunne ikke laste øvelser: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const confirmSeedFromPreviousYear = async () => {
    if (isReadOnly) return;
    const prev = year - 1;
    try {
      setSeeding(true);
      await RestService.post<{}, { Success: boolean; Inserted: number }>(
        `/api/athleticsregistration/SeedFromPreviousYear?year=${year}`,
        {},
      );
      await loadEvents(year);
      toast.success(`Data fra ${prev} ble hentet inn for ${year}.`);
      setSeedDialogOpen(false);
    } catch (e) {
      toast.error(`Kunne ikke hente data: ${e}`);
    } finally {
      setSeeding(false);
    }
  };

  const ages = Array.from({ length: 15 }, (_, i) => i + 4);

  const toggleCell = async (eventId: number, age: number, nextEligible: boolean) => {
    if (isReadOnly) return;
    const key = `${eventId}-${age}`;
    if (pendingKeys.has(key)) return;

    setEvents(prev => prev.map(e => {
      if (e.Id !== eventId) return e;
      const newAges = nextEligible
        ? Array.from(new Set([...e.Ages, age])).sort((a, b) => a - b)
        : e.Ages.filter(a => a !== age);
      return { ...e, Ages: newAges };
    }));
    setPendingKeys(prev => new Set(prev).add(key));

    try {
      await RestService.post<Api.SetEligibilityRequest, void>(
        '/api/athleticsregistration/SetEligibility',
        { Year: year, Age: age, AthleticsEventId: eventId, Eligible: nextEligible },
      );
      setLastSavedAt(new Date());
    } catch (e) {
      setEvents(prev => prev.map(ev => {
        if (ev.Id !== eventId) return ev;
        const rolled = !nextEligible
          ? Array.from(new Set([...ev.Ages, age])).sort((a, b) => a - b)
          : ev.Ages.filter(a => a !== age);
        return { ...ev, Ages: rolled };
      }));
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setPendingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const cellStyle: React.CSSProperties = { border: '1px solid #888', padding: '8px 6px', textAlign: 'center', fontSize: '.9rem', wordBreak: 'break-word' };
  const headStyle: React.CSSProperties = { ...cellStyle, background: '#eee', whiteSpace: 'normal' };
  const rowHeadStyle: React.CSSProperties = { ...cellStyle, background: '#eee', fontWeight: 600 };
  const checkboxCellStyle: React.CSSProperties = { ...cellStyle, padding: '8px 0' };

  return (
    <>
      <OKomBreadcrumb path='Konfigurasjon, Friidrettsregistrering'></OKomBreadcrumb>
      {isLoading && <OKomLoading text="Laster..." />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12 ods-text--align-center">
          <div className="ods-select" style={{ display: 'inline-block', width: '12rem', textAlign: 'left' }}>
            <div className="ods-select__select">
              <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}>
                {allYears.map(y => (
                  <option key={y} value={y}>{y}{y === currentYear ? ' (i år)' : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {isReadOnly && (
            <p style={{ marginTop: '.5rem', color: '#a04040' }}>
              Tidligere år kan ikke endres. Velg gjeldende eller fremtidig år for å redigere.
            </p>
          )}
          {!isReadOnly && (
            <div className="ods-margin-top-3">
              <button
                type="button"
                className="ods-button ods-button--red"
                onClick={() => setSeedDialogOpen(true)}
              >
                <span className="ods-button__icon ods-button__icon--left ods-icon--warning-triangle" aria-hidden="true"></span>
                Hent data fra forrige år
              </button>
            </div>
          )}
          {lastSavedAt && (
            <p className="ods-text--align-center ods-margin-top-3" style={{ fontSize: '.875rem' }}>
              <span key={lastSavedAt.getTime()} className="okom-flash-saved">
                Sist lagret: {String(lastSavedAt.getHours()).padStart(2, '0')}:
                {String(lastSavedAt.getMinutes()).padStart(2, '0')}:
                {String(lastSavedAt.getSeconds()).padStart(2, '0')}
              </span>
            </p>
          )}
        </div>
        <div className="ods-grid__column--12">
          <table style={{ borderCollapse: 'collapse', margin: '0 auto', background: '#fff', tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                <th style={headStyle}>Fødselsår</th>
                <th style={headStyle}>Aldersgruppe</th>
                {events.map(e => (
                  <th key={e.Id} style={headStyle}>{e.NameNo}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ages.map(a => {
                const bg = backgroundForAge(a);
                return (
                  <tr key={a} style={{ background: bg }}>
                    <th style={{ ...rowHeadStyle, background: bg }}>{year - a}</th>
                    <th style={{ ...rowHeadStyle, background: bg }}>{a}</th>
                    {events.map(e => {
                      const eligible = e.Ages.includes(a);
                      return (
                        <td key={e.Id} style={checkboxCellStyle}>
                          <div style={{ display: 'flex', justifyContent: 'center', opacity: isReadOnly ? 0.55 : 1, pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                            <OKomCheckbox
                              key={`${e.Id}-${a}-${eligible}-${year}`}
                              checked={eligible}
                              onChange={(c) => toggleCell(e.Id, a, c)}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <OKomModal
        open={seedDialogOpen}
        onClose={() => { if (!seeding) setSeedDialogOpen(false); }}
        labelledBy="seed-dialog-heading"
      >
        <div className="ods-grid ods-grid--gap">
          <div className="ods-grid__column--12">
            <h3
              id="seed-dialog-heading"
              className="ods-text--size-hotel ods-text--size-foxtrot-breakpoint-medium ods-text--size-delta-breakpoint-large ods-margin-bottom-8"
            >
              Hent data fra forrige år
            </h3>
            <p>
              Vil du erstatte øvelsesoppsettet for <strong>{year}</strong> med data fra <strong>{year - 1}</strong>?
            </p>
            <p className="ods-margin-top-2">
              Alle eksisterende valg for {year} vil bli slettet og erstattet.
            </p>
          </div>
          <div className="ods-grid__column--12">
            <button
              type="button"
              className="ods-button ods-button--red ods-margin-right-3"
              onClick={confirmSeedFromPreviousYear}
              disabled={seeding}
            >
              <span className="ods-button__icon ods-button__icon--left ods-icon--warning-triangle" aria-hidden="true"></span>
              Hent data
            </button>
            <button
              type="button"
              className="ods-button ods-button--outline"
              onClick={() => setSeedDialogOpen(false)}
              disabled={seeding}
            >
              Avbryt
            </button>
          </div>
        </div>
      </OKomModal>
    </>
  );
};

export default withRouter(AthleticsRegistrationConfigView);
